import { rangesEqual, verseRange, withTranslation } from "~/units/scripture";
/**
 * @file
 * Commentary grouping algorithm.
 * See https://refstudycentre.atlassian.net/wiki/spaces/VV/pages/2062942310/Grouping+and+sorting
 */
import type {
  FlatCommentaryGroup,
  InnerCommentaryGroup,
  OuterCommentaryGroup,
  ProcessedCommentaryComment,
  ProcessedCommentaryDocList,
  ProcessedCommentaryImage,
} from "./types";
import {
  compareComments,
  compareInnerGroups,
  compareOuterGroups,
} from "~/composables/processCommentarySearchResults/sorting";

/**
 * An empty doc list, useful when creating fake groups on the front end.
 */
export const emptyDocList: ProcessedCommentaryDocList = {
  docs: [],
  numFound: 0,
  numFoundExact: true,
  start: 0,
};

/**
 * Nest and sort commentary groups.
 *
 * This moves inner groups into the outer groups, and sorts both the outer and the inner groups.
 * - Inner groups are groups that reference anything translation-specific.
 * - Outer groups are the rest.
 * - An inner group's parent is an outer group that references the same range, but is translation-agnostic.
 */
export function nestAndSortCommentary(
  flatGroups: FlatCommentaryGroup[],
  narrow: boolean,
  languages: string[],
): OuterCommentaryGroup[] {
  /**
   * Turn a group into an inner group.
   */
  function inner(g: FlatCommentaryGroup): InnerCommentaryGroup {
    return {
      range: g.range,
      verse: g.verse,
      versePart: g.versePart,
      doclist: g.doclist,
    };
  }

  /**
   * Turn a group into an outer group.
   */
  function outer(g: FlatCommentaryGroup): OuterCommentaryGroup {
    return {
      heading: g.heading,
      range: g.range,
      subGroups: [],
      doclist: g.doclist,
    };
  }

  /**
   * Find an appropriate parent for `g` in `outerGroups` and add it to the parent's subgroups.
   * If an appropriate parent is not found, generate one.
   *
   * @param outerGroups
   * @param ig
   * @param predicate
   *   A predicate used for finding a parent in outerGroups.
   */
  function nest(
    outerGroups: OuterCommentaryGroup[],
    ig: InnerCommentaryGroup,
    predicate: (x: OuterCommentaryGroup) => boolean,
  ) {
    const parent = outerGroups.find(predicate);
    if (parent) {
      parent.subGroups.push(ig);
    } else {
      outerGroups.push({
        range:
          (ig.verse
            ? withTranslation(verseRange(ig.verse), null)
            : undefined) ??
          (ig.range == null ? undefined : withTranslation(ig.range, null)),
        subGroups: [ig],
        doclist: emptyDocList,
      });
    }
  }

  function isInnerGroup(g: FlatCommentaryGroup): boolean {
    return !!(g.verse || g.range?.tid);
  }

  const innerGroups: InnerCommentaryGroup[] = flatGroups
    .filter(isInnerGroup)
    .map(inner);

  const outerGroups: OuterCommentaryGroup[] = flatGroups
    .filter((g) => !isInnerGroup(g))
    .map(outer);

  // For each inner group, find or create an appropriate parent group.
  innerGroups.forEach((ig) => {
    if (ig.verse) {
      const verse = ig.verse;
      nest(outerGroups, ig, (og) =>
        rangesEqual(withTranslation(verseRange(verse), null), og.range),
      );
    } else if (ig.range?.tid) {
      const range = ig.range;
      nest(outerGroups, ig, (og) =>
        rangesEqual(withTranslation(range, null), og.range),
      );
    } else {
      throw new Error("Invalid inner group.");
    }
  });

  return outerGroups
    .sort((a, b) => compareOuterGroups(a, b, narrow))
    .map((og: OuterCommentaryGroup) => {
      og.subGroups = og.subGroups
        .sort((a, b) => compareInnerGroups(a, b, narrow, languages))
        .map((ig) => {
          ig.doclist.docs = ig.doclist.docs.sort((a, b) =>
            compareComments(
              a,
              b,
              narrow,
              ig.range || (ig.verse == null ? undefined : verseRange(ig.verse)),
              languages,
            ),
          );
          return ig;
        });
      og.doclist.docs = og.doclist.docs.sort((a, b) =>
        compareComments(a, b, narrow, og.range, languages),
      );
      return og;
    });
}

export function getFlatResultsFromGroups(
  outerGroups: OuterCommentaryGroup[],
): (ProcessedCommentaryComment | ProcessedCommentaryImage)[] {
  return outerGroups.reduce(
    (
      outerResults: (ProcessedCommentaryComment | ProcessedCommentaryImage)[],
      outerGroup: OuterCommentaryGroup,
    ) => {
      const nidMap: { [key: number]: boolean | undefined } = {};
      const newItems: (
        | ProcessedCommentaryComment
        | ProcessedCommentaryImage
      )[] = [
        ...outerGroup.doclist.docs,
        ...outerGroup.subGroups.reduce(
          (
            innerResults: (
              | ProcessedCommentaryComment
              | ProcessedCommentaryImage
            )[],
            innerGroup: InnerCommentaryGroup,
          ) => {
            return [...innerResults, ...innerGroup.doclist.docs];
          },
          [],
        ),
      ].reduce(
        (
          acc: (ProcessedCommentaryComment | ProcessedCommentaryImage)[],
          current: ProcessedCommentaryComment | ProcessedCommentaryImage,
        ) => {
          const nid = current.nid;
          if (nidMap[nid]) {
            return acc;
          }
          nidMap[nid] = true;
          acc.push(current);
          return acc;
        },
        [],
      );

      return [...outerResults, ...newItems];
    },
    [],
  );
}
